<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import WeChatTipBanner from '@/components/WeChatTipBanner.vue'

const route = useRoute()
// 'forward' 进栈 / 'backward' 回首页；初始为空字符串，避免首屏渲染就触发
// transition enter，导致 enter-from 类卡住、页面透明看不见控件。
// 只有路由真正切换时才设为 'forward' / 'backward'。
const transitionName = ref<'forward' | 'backward' | ''>('')

// 微信内置浏览器不支持麦克风，命中时整页替换为居中提示，隐藏所有业务 UI。
function detectWeChat(): boolean {
  if (typeof navigator === 'undefined') return false
  return /MicroMessenger/i.test(navigator.userAgent)
}
const wechatBlocked = ref(false)

onMounted(() => {
  wechatBlocked.value = detectWeChat()
})

watch(
  () => route.path,
  (newPath, oldPath) => {
    // 进入 /assistant（首页外的任意路径）= forward
    // 回到 / = backward
    if (newPath !== '/' && oldPath === '/') {
      transitionName.value = 'forward'
    } else if (newPath === '/' && oldPath !== '/') {
      transitionName.value = 'backward'
    } else {
      // 兜底：其他跳转维持 forward
      transitionName.value = 'forward'
    }
  },
)
</script>

<template>
  <!-- 全屏背景层：承载三色柔光球渐变。
       用真实 div 而非 ::before 伪元素：iOS PWA 下伪元素 z-index 行为不稳定、
       GPU 合成层会丢失。div 用 z-index:0 + pointer-events:none 稳定渲染在内容之下。 -->
  <div class="xw-fullpage-bg" aria-hidden="true" />

  <!-- 微信下整页替换为居中提示，隐藏所有业务 UI -->
  <WeChatTipBanner v-if="wechatBlocked" />
  <router-view v-else v-slot="{ Component }" class="router-view-wrap">
    <!-- 走马灯过渡：用动态 transitionName，根据前进/返回应用不同方向。
         首屏 transitionName 为空字符串 → 不渲染任何 transition 类，
         避免首屏 enter-from 起始帧卡住导致控件透明不可见。 -->
    <transition v-if="transitionName" :name="transitionName">
      <component :is="Component" />
    </transition>
    <component v-else :is="Component" />
  </router-view>
</template>

<style lang="scss">
/* 根容器：撑到物理屏底（100vh + safe-top + safe-bottom） */
#app {
  min-height: calc(100vh + env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px));
  min-height: calc(100dvh + env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
}

/* 全屏背景层：fixed + 撑到物理屏底，三色柔光球渐变铺满整个物理屏。
   z-index:0 + pointer-events:none 让背景在内容之下、不阻挡交互。 */
.xw-fullpage-bg {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  // iOS PWA standalone 下 100vh = innerHeight（873px），不含 safe-area。
  // 撑到物理屏底：100vh + safe-top + safe-bottom = 966px，
  // 让背景层覆盖整个物理屏，避免 safe-area 区露出灰底。
  height: calc(100vh + env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px));
  height: calc(100dvh + env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px));
  z-index: 0;
  pointer-events: none;
  opacity: 1;
  // 兑底：使用 --xw-blob-pink-edge（粉色兑底）而非 --xw-page-bg（接近白色）。
  // 为什么？
  //   .xw-fullpage-bg 的 background-image 多层 radial 渐变，
  //   radial 末梢是 transparent——但 viewport 边缘（远离 radial 中心的区域）
  //   会露出 background-color。
  //   如果兑底是 --xw-page-bg（接近白色），露出的兑底是白色，
  //   与上方 radial 渐变（粉色 radial 末端是粉色兑底）之间有色阶差，
  //   形成"底部兑底块"与"上方 radial 区域"之间的明显横线分割。
  //   改用 --xw-blob-pink-edge（粉色兑底）后，露出的兑底色 = radial 末端色，
  //   两者完全同色，无视觉边界，"底部兑底块"和上方 radial 自然衔接成一片。
  background-color: var(--xw-blob-pink-edge);
  // 三色柔光球渐变（radial 末梢 transparent，中心强色相 → 边缘自然过渡）。
  // 沿用项目最初版本（git init 那一版）的渐变配置，保留视觉风格一致：
  //   - 顶部 at 20% 0%：蓝色 radial（顶左）
  //   - 中部 at 90% 25%：紫色 radial（右上）
  //   - 底部 at 30% 108%：粉色 radial（底中、中心点略超出 viewport 让边缘柔和）
  background-image:
    // 顶部蓝色 radial：扩大到 90% 80%，让蓝色光晕覆盖整个上半部分 0~40% 高、横向 0~65% 区域
    radial-gradient(90% 80% at 20% 0%, var(--xw-blob-blue) 0%, var(--xw-blob-blue-edge) 60%, transparent 90%),
    // 右上紫色 radial：中心点移到 at 75% 25%，让紫色覆盖 45~105% 横向区域，
    // 与蓝色 radial 在 45~65% 横向重叠，形成自然的蓝→紫横向过渡，
    // 避免"左上蓝、右上紫"的明显割裂感。
    radial-gradient(60% 55% at 75% 25%, var(--xw-blob-purple) 0%, var(--xw-blob-purple-edge) 60%, transparent 90%),
    // 底部粉色 radial：中心点 y=108%（稍超出 viewport 下边缘），
    // 让 pink 在 viewport 底部最浓，68% 58% 的椭圆宽度让粉色光晕更饱满。
    radial-gradient(68% 58% at 30% 108%, var(--xw-blob-pink) 0%, var(--xw-blob-pink-edge) 62%, transparent 92%);
  background-size: cover;
  background-repeat: no-repeat;
}

/* 让 router-view 内的内容在背景层之上 + <router-view> 作为绝对定位父级 */
.router-view-wrap {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  overflow: hidden;
}

/* 通用：过渡期间让两个页面绝对定位叠在同一位置 */
.forward-enter-active,
.forward-leave-active,
.backward-enter-active,
.backward-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s cubic-bezier(0.22, 0.61, 0.36, 1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
}

/* ===== forward（首页 → assistant）：新页从右向左挤入 ===== */
.forward-enter-from {
  opacity: 0;
  transform: translateX(100%);
}
.forward-enter-to {
  opacity: 1;
  transform: translateX(0);
}
.forward-leave-from {
  opacity: 1;
  transform: translateX(0);
}
.forward-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

/* ===== backward（assistant → 首页）：新首页从左向右挤入 ===== */
.backward-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}
.backward-enter-to {
  opacity: 1;
  transform: translateX(0);
}
.backward-leave-from {
  opacity: 1;
  transform: translateX(0);
}
.backward-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
